import React, { useEffect, useState } from 'react';
import { FiFolder, FiEdit2, FiEye, FiDownload, FiTrash2, FiRefreshCw, FiPlus, FiCopy, FiMoreVertical } from 'react-icons/fi';
import './Projects.css';
import AxiosInstance from '../../AxiosInstance';
import config from '../../config/frontend.config';
import { useNavigate } from 'react-router-dom';
import showToast from '../../components/Toast';
import { usePermissions } from '../../contexts/PermissionContext';

const getTemplateThumbnail = (template) => {
  if (template.image) {
    return template.image;
  }
  // Use ui-avatars.com to generate a fallback image based on the template name
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(template.templateName)}&background=random&size=320`;
};

const TemplateCard = ({ template, onEdit, onView, onViewTemplate, onExport, onDelete, onCopy, onThreeDotMenu, canEdit, canDelete, canCopy, canView }) => {
  const templateName = template.templateName || 'Untitled Template';
  const [showDropdown, setShowDropdown] = useState(false);
  
  const handleThreeDotClick = (e) => {
    e.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  const handleActionClick = (action) => {
    setShowDropdown(false);
    switch (action) {
      case 'view':
        onView(template);
        break;
      case 'viewTemplate':
        if (onViewTemplate) {
          onViewTemplate(template);
        } else {
          onView(template);
        }
        break;
      case 'export':
        onExport(template);
        break;
      case 'copy':
        onCopy(template);
        break;
      case 'edit':
        onEdit(template);
        break;
      case 'delete':
        onDelete(template);
        break;
      default:
        break;
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setShowDropdown(false);
    };

    if (showDropdown) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showDropdown]);
  
  return (
    <div className="template-card">
      <div className="template-image">
        <img 
          src={getTemplateThumbnail(template)} 
          alt={templateName} 
          className="template-thumbnail"
          style={{
            width: '100%',
            height: '220px',
            objectFit: 'cover',
            display: 'block',
            borderRadius: '20px 20px 0 0',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
          }}
        />
        
        {/* Three-dot menu button */}
        <button 
          className={`three-dot-menu ${showDropdown ? 'active' : ''}`}
          onClick={handleThreeDotClick}
          aria-label="More options"
          style={{
            position: 'absolute',
            top: '15px',
            right: '15px',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '50%',
            width: '40px',
            height: '40px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            zIndex: 10,
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.15)',
            color: '#6366f1',
            fontSize: '18px'
          }}
        >
          <FiMoreVertical />
        </button>

        {/* Dropdown Menu */}
        {showDropdown && (
          <div 
            className="dropdown-menu"
            style={{
              position: 'absolute',
              top: '55px',
              right: '15px',
              background: 'white',
              borderRadius: '16px',
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
              border: '1px solid rgba(0, 0, 0, 0.1)',
              padding: '0',
              minWidth: '220px',
              zIndex: 1000,
              overflow: 'hidden'
            }}
          >
            {canView && (
              <button 
                className="dropdown-item view"
                onClick={() => handleActionClick('view')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  width: '100%',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  color: '#374151',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'left',
                  borderBottom: '1px solid #f3f4f6'
                }}
              >
                <FiEye /> View Details
              </button>
            )}
            {canView && (
              <button 
                className="dropdown-item export"
                onClick={() => handleActionClick('export')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  width: '100%',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  color: '#374151',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'left',
                  borderBottom: '1px solid #f3f4f6'
                }}
              >
                <FiDownload /> Export Template
              </button>
            )}
            {canCopy && (
              <button 
                className="dropdown-item copy"
                onClick={() => handleActionClick('copy')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  width: '100%',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  color: '#374151',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'left',
                  borderBottom: '1px solid #f3f4f6'
                }}
              >
                <FiCopy /> Duplicate Template
              </button>
            )}
            {canView && (
              <button 
                className="dropdown-item view"
                onClick={() => handleActionClick('viewTemplate')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  width: '100%',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  color: '#374151',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'left',
                  borderBottom: '1px solid #f3f4f6'
                }}
              >
                <FiEye /> View Template
              </button>
            )}
            {canEdit && (
              <button 
                className="dropdown-item edit"
                onClick={() => handleActionClick('edit')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  width: '100%',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  color: '#374151',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'left',
                  borderBottom: '1px solid #f3f4f6'
                }}
              >
                <FiEdit2 /> Edit Template
              </button>
            )}
            {canDelete && (
              <button 
                className="dropdown-item delete"
                onClick={() => handleActionClick('delete')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  width: '100%',
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  color: '#374151',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textAlign: 'left',
                  borderBottom: '1px solid #f3f4f6'
                }}
              >
                <FiTrash2 /> Delete Template
              </button>
            )}
          </div>
        )}
      </div>
      
      <div className="template-info" style={{
        padding: '24px',
        background: 'white',
        borderRadius: '0 0 20px 20px',
        borderTop: '1px solid rgba(0, 0, 0, 0.05)'
      }}>
        <h3 className="template-name" style={{
          fontSize: '1.25rem',
          fontWeight: '700',
          color: '#1f2937',
          margin: '0 0 8px 0',
          lineHeight: '1.3'
        }}>
          {templateName}
        </h3>
        <p className="template-description" style={{
          color: '#6b7280',
          fontSize: '0.9rem',
          margin: '0 0 12px 0',
          lineHeight: '1.5'
        }}>
          {template.description || 'No description available'}
        </p>
        <div className="template-meta" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          color: '#9ca3af',
          fontSize: '0.85rem'
        }}>
          <span style={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}>
            📅 {template.updatedAt ? new Date(template.updatedAt).toLocaleDateString() : 'No date'}
          </span>
        </div>
      </div>
    </div>
  );
};

const Projects = () => {
  const { hasPermission, permissions, userRole } = usePermissions();
  

  const [templates, setTemplates] = useState([]);
  const [templateError, setTemplateError] = useState(null);
  const [loading, setLoading] = useState(false);
  // Get userId using config helper
  const userId = config.getUserId();
  // const userId = user?.UserId;
  const navigate = useNavigate();
  

  
  const baseUrl = config.api.baseURL;

  // Modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [templateToCopy, setTemplateToCopy] = useState(null);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [isCopying, setIsCopying] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [templateToView, setTemplateToView] = useState(null);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [templateForOptions, setTemplateForOptions] = useState(null);

  const fetchTemplates = async () => {
    if (!userId) {
      return;
    }
    
    // Check if user has permission to view projects
    if (!hasPermission('canViewProjects')) {
      setTemplateError('You do not have permission to view projects');
      setTemplates([]);
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const apiUrl = `${baseUrl}/${config.api.apiPath}/templates/user/${userId}`;
      const response = await AxiosInstance.get(apiUrl);
      
      // Handle new API response structure
      if (response.data && response.data.statusCode === 200) {
        setTemplates(response.data.data || []);
        setTemplateError(null);
      } else if (response.data && response.data.statusCode === 204) {
        setTemplates([]);
        setTemplateError(response.data.message || 'No templates found.');
      } else {
        setTemplateError(response.data?.message || 'No templates found.');
        setTemplates([]);
      }
    } catch (err) {
      if (err.response && err.response.status === 403) {
        setTemplateError('You are not an active team member, so you cannot view templates.');
      } else if (err.response && err.response.data && err.response.data.message) {
        setTemplateError(err.response.data.message);
      } else {
        setTemplateError('Failed to fetch templates.');
      }
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [userId]);



  const handleEdit = (template) => {
    // Check if user has permission to edit projects
    if (!hasPermission('canEditProjects')) {
      showToast.error('You do not have permission to edit projects');
      return;
    }

    const idToUse = template.TemplateId || template.templateId || template._id;
    if (!idToUse) {
      showToast.error('Template ID not found. Cannot edit template.');
      return;
    }
    
    showToast.info('Opening template editor...');
    // Navigate to BuilderJS6 with template ID - it will load the template data automatically
    navigate(`/builderjs6/${idToUse}`);
  };

  const handleView = (template) => {
    // Check if user has permission to view projects
    if (!hasPermission('canViewProjects')) {
      showToast.error('You do not have permission to view projects');
      return;
    }
    setTemplateToView(template);
    setShowViewModal(true);
  };

  const handleViewTemplate = (template) => {
    // Check if user has permission to view projects
    if (!hasPermission('canViewProjects')) {
      showToast.error('You do not have permission to view templates');
      return;
    }

    const idToUse = template.TemplateId || template.templateId || template._id;
    if (!idToUse) {
      showToast.error('Template ID not found. Cannot view template.');
      return;
    }
    
    showToast.info('Opening template in view mode...');
    // Navigate to BuilderJS6 view mode with template ID
    navigate(`/builderjs6/view/${idToUse}`);
  };
  
  const handleExport = (template) => {
    // Check if user has permission to view projects (export is a view action)
    if (!hasPermission('canViewProjects')) {
      showToast.error('You do not have permission to export projects');
      return;
    }
    showToast.success('Export: ' + template.templateName);
  };
  
  const handleDelete = (template) => {
    // Check if user has permission to delete projects
    if (!hasPermission('canDeleteProjects')) {
      showToast.error('You do not have permission to delete projects');
      return;
    }

    setTemplateToDelete(template);
    setShowDeleteModal(true);
  };

  const handleCopy = (template) => {
    // Check if user has permission to create projects (copying creates a new project)
    if (!hasPermission('canCreateProjects')) {
      showToast.error('You do not have permission to copy projects');
      return;
    }

    setTemplateToCopy(template);
    setNewTemplateName(`${template.templateName} (Copy)`);
    setShowCopyModal(true);
  };

  const handleThreeDotMenu = (template, event) => {
    event.stopPropagation();
    setTemplateForOptions(template);
    setShowOptionsModal(true);
  };

  const confirmDelete = async () => {
    try {
      // Use the correct API endpoint with apiPath
      const deleteUrl = `${baseUrl}/${config.api.apiPath}/templates/${templateToDelete.TemplateId || templateToDelete._id}`;
      
      const response = await AxiosInstance.delete(deleteUrl, { 
        data: { 
          reason: deleteReason,
          userId: userId // Add userId for proper authorization
        } 
      });
      
      // Handle new API response structure
      if (response.data && response.data.statusCode === 200) {
        showToast.success(response.data.message || `Template "${templateToDelete.templateName}" deleted successfully!`);
        // Remove template from local state using both possible ID fields
        setTemplates(templates.filter(t => 
          t._id !== templateToDelete._id && 
          t.TemplateId !== templateToDelete.TemplateId
        ));
        // Refresh templates to ensure consistency
        setTimeout(() => fetchTemplates(), 1000);
      } else {
        showToast.error(response.data?.message || 'Failed to delete template.');
      }
    } catch (err) {
      // Toast notification is handled by AxiosInstance interceptor
    } finally {
      setShowDeleteModal(false);
      setDeleteReason('');
      setTemplateToDelete(null);
    }
  };

  const closeModal = () => {
    setShowDeleteModal(false);
    setDeleteReason('');
    setTemplateToDelete(null);
  };

  const confirmCopy = async () => {
    if (!newTemplateName.trim()) {
      showToast.error('Please enter a template name');
      return;
    }

    try {
      setIsCopying(true);
      const copyUrl = `${baseUrl}/${config.api.apiPath}/templates/${templateToCopy.TemplateId || templateToCopy._id}/copy`;
      
      const response = await AxiosInstance.post(copyUrl, { 
        newTemplateName: newTemplateName.trim(),
        userId: userId
      });
      
      if (response.data && response.data.statusCode === 201) {
        showToast.success(response.data.message || `Template "${templateToCopy.templateName}" copied successfully!`);
        // Refresh templates to show the new copy
        setTimeout(() => fetchTemplates(), 1000);
      } else {
        showToast.error(response.data?.message || 'Failed to copy template.');
      }
    } catch (err) {
      // Toast notification is handled by AxiosInstance interceptor
    } finally {
      setIsCopying(false);
      setShowCopyModal(false);
      setNewTemplateName('');
      setTemplateToCopy(null);
    }
  };

  const closeCopyModal = () => {
    setShowCopyModal(false);
    setNewTemplateName('');
    setTemplateToCopy(null);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setTemplateToView(null);
  };

  const closeOptionsModal = () => {
    setShowOptionsModal(false);
    setTemplateForOptions(null);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await fetchTemplates();
      showToast.success('Templates refreshed successfully!');
    } catch (error) {
      showToast.error('Failed to refresh templates');
    } finally {
      setIsRefreshing(false);
    }
  };

  // Load templates for selection
  const loadTemplatesForSelection = async () => {
    try {
      setTemplatesLoading(true);
      const userId = config.getUserId();
      
      // Use the same API endpoint as fetchTemplates for consistency
      const apiUrl = `${baseUrl}/${config.api.apiPath}/templates/user/${userId}`;
      
      const response = await AxiosInstance.get(apiUrl);
      
      if (response.data?.statusCode === 200) {
        // Update the templates state with the loaded data
        setTemplates(response.data.data || []);
      } else if (response.data?.statusCode === 204) {
        setTemplates([]);
      } else {
        setTemplates([]);
      }
    } catch (error) {
      showToast.error('Failed to load templates');
      setTemplates([]);
    } finally {
      setTemplatesLoading(false);
    }
  };

  // Select template for use
  const selectTemplate = (template) => {
    showToast.success(`Template "${template.templateName}" selected!`);
    setShowTemplateModal(false);
    // You can add additional logic here for what happens when a template is selected
    // For example, navigate to email builder with this template
    const idToUse = template.TemplateId || template.templateId || template._id;
    navigate(`/email-builder/${idToUse}`);
  };









  // Create sample templates
  const createSampleTemplates = async () => {
    try {
      const userId = config.getUserId();
      const sampleTemplates = [
        {
          userId: userId,
          templateName: "Welcome Email Template",
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background: #ffffff;">
              <div style="text-align: center; margin-bottom: 30px;">
                <h1 style="color: #333; margin: 0; font-size: 28px;">Welcome to Our Company!</h1>
                <p style="color: #666; margin: 10px 0 0 0; font-size: 16px;">We're excited to have you on board</p>
              </div>
              
              <div style="background: #f8f9fa; padding: 25px; border-radius: 10px; margin: 20px 0;">
                <h2 style="color: #333; margin-top: 0;">Getting Started</h2>
                <p style="color: #666; line-height: 1.6;">
                  Dear [Customer Name],<br><br>
                  Thank you for choosing us! We're thrilled to have you as part of our community.
                </p>
                
                <div style="background: #ffffff; padding: 20px; border-radius: 8px; margin: 20px 0;">
                  <h3 style="color: #333; margin-top: 0;">What's Next?</h3>
                  <ul style="color: #666; line-height: 1.8;">
                    <li>📧 Complete your profile setup</li>
                    <li>🔍 Explore our products and services</li>
                    <li>👥 Connect with our support team</li>
                    <li>📰 Stay updated with our latest news</li>
                  </ul>
                </div>
              </div>
              
              <div style="text-align: center; margin-top: 30px;">
                <p style="color: #666; line-height: 1.6;">
                  If you have any questions, feel free to reach out to us at support@company.com
                </p>
                <p style="color: #666; line-height: 1.6;">
                  Best regards,<br>
                  <strong>The Team</strong>
                </p>
              </div>
            </div>
          `,
          json: { blocks: [] }
        },
        {
          userId: userId,
          templateName: "Newsletter Template",
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background: #ffffff;">
              <div style="text-align: center; margin-bottom: 30px;">
                <h1 style="color: #2c3e50; margin: 0; font-size: 32px;">📰 Monthly Newsletter</h1>
                <p style="color: #7f8c8d; margin: 10px 0 0 0; font-size: 16px;">Stay updated with our latest news and updates</p>
              </div>
              
              <div style="background: #ecf0f1; padding: 25px; border-radius: 10px; margin: 20px 0;">
                <h2 style="color: #2c3e50; margin-top: 0;">This Month's Highlights</h2>
                
                <div style="background: #ffffff; padding: 20px; border-radius: 8px; margin: 15px 0;">
                  <h3 style="color: #e74c3c; margin-top: 0;">🚀 New Features Released</h3>
                  <p style="color: #34495e; line-height: 1.6;">
                    We've launched exciting new features to enhance your experience. Check them out!
                  </p>
                </div>
                
                <div style="background: #ffffff; padding: 20px; border-radius: 8px; margin: 15px 0;">
                  <h3 style="color: #27ae60; margin-top: 0;">📈 Performance Updates</h3>
                  <p style="color: #34495e; line-height: 1.6;">
                    Our platform is now faster and more reliable than ever before.
                  </p>
                </div>
                
                <div style="background: #ffffff; padding: 20px; border-radius: 8px; margin: 15px 0;">
                  <h3 style="color: #3498db; margin-top: 0;">🎉 Community Spotlight</h3>
                  <p style="color: #34495e; line-height: 1.6;">
                    Meet our amazing community members and their success stories.
                  </p>
                </div>
              </div>
              
              <div style="text-align: center; margin-top: 30px;">
                <p style="color: #7f8c8d; line-height: 1.6;">
                  Thank you for being part of our community!<br>
                  <strong>The Newsletter Team</strong>
                </p>
              </div>
            </div>
          `,
          json: { blocks: [] }
        },
        {
          userId: userId,
          templateName: "Promotional Email",
          html: `
            <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; padding: 20px; background: #ffffff;">
              <div style="text-align: center; margin-bottom: 30px;">
                <h1 style="color: #e74c3c; margin: 0; font-size: 32px;">🔥 Special Offer!</h1>
                <p style="color: #7f8c8d; margin: 10px 0 0 0; font-size: 18px;">Limited time deal - Don't miss out!</p>
              </div>
              
              <div style="background: linear-gradient(135deg, #e74c3c, #c0392b); padding: 30px; border-radius: 15px; margin: 20px 0; text-align: center;">
                <h2 style="color: #ffffff; margin-top: 0; font-size: 28px;">🎉 50% OFF</h2>
                <p style="color: #ffffff; font-size: 20px; margin: 10px 0;">Premium Package</p>
                <p style="color: #ffffff; font-size: 16px; margin: 10px 0;">Valid until [End Date]</p>
                
                <div style="background: #ffffff; padding: 20px; border-radius: 10px; margin: 20px 0; display: inline-block;">
                  <h3 style="color: #e74c3c; margin-top: 0;">What's Included:</h3>
                  <ul style="color: #2c3e50; text-align: left; line-height: 1.8;">
                    <li>✅ Advanced features</li>
                    <li>✅ Priority support</li>
                    <li>✅ Custom integrations</li>
                    <li>✅ Analytics dashboard</li>
                  </ul>
                </div>
                
                <button style="background: #ffffff; color: #e74c3c; border: none; padding: 15px 30px; border-radius: 25px; font-size: 18px; font-weight: bold; cursor: pointer; margin-top: 20px;">
                  🛒 Claim Offer Now
                </button>
              </div>
              
              <div style="text-align: center; margin-top: 30px;">
                <p style="color: #7f8c8d; line-height: 1.6;">
                  This offer expires soon! Don't wait too long.<br>
                  <strong>Sales Team</strong>
                </p>
              </div>
            </div>
          `,
          json: { blocks: [] }
        }
      ];

      let createdCount = 0;
      for (const template of sampleTemplates) {
        try {
          // Use the correct API endpoint with apiPath
          const createUrl = `${baseUrl}/${config.api.apiPath}/templates`;
          
          const response = await AxiosInstance.post(createUrl, template);
          
          if (response.data?.statusCode === 201) {
            createdCount++;
          }
        } catch (error) {
          // Error handled by toast notification
        }
      }
      
      if (createdCount > 0) {
        showToast.success(`${createdCount} sample templates created successfully!`);
        fetchTemplates(); // Reload templates
      } else {
        showToast.error('Failed to create sample templates');
      }
    } catch (error) {
      showToast.error('Failed to create sample templates');
    }
  };

  return (
    <div className="page-main">
              <div className="page-title-row">
          <h1><FiFolder className="page-title-icon" size={28} />{" "}My Templates</h1>
        </div>
        
        <div className="project-controls">
          <select className="project-select">
            <option>Last updated</option>
            <option>Name A-Z</option>
            <option>Name Z-A</option>
            <option>Recently created</option>
          </select>
          <input className="project-search" placeholder="Search templates..." />
          <button className="project-type">All Templates</button>
        <button 
          className="refresh-btn"
          onClick={handleRefresh}
          disabled={isRefreshing}
          title="Refresh templates"
        >
          <FiRefreshCw className={isRefreshing ? 'spinning' : ''} />
          {isRefreshing ? '↻...' : '↻'}
        </button>
      </div>
             <div>
                  {loading ? (
            <div className="empty-state">
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⏳</div>
              <h3>Loading Templates</h3>
              <p>Please wait while we fetch your templates...</p>
            </div>
          ) : templateError ? (
            <div className="empty-state">
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
              <h3>Error Loading Templates</h3>
              <p>{templateError}</p>
              <button 
                onClick={handleRefresh}
                style={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  marginTop: '1rem'
                }}
              >
                Try Again
              </button>
            </div>
          ) : (
          <div className="template-grid">
            {templates.length > 0 ? (
              templates.map(t => (
                <TemplateCard
                  key={t._id}
                  template={t}
                  onEdit={handleEdit}
                  onView={handleView}
                  onViewTemplate={handleViewTemplate}
                  onExport={handleExport}
                                      onDelete={handleDelete}
                    onCopy={handleCopy}
                    onThreeDotMenu={handleThreeDotMenu}
                  canEdit={hasPermission('canEditProjects')}
                  canDelete={hasPermission('canDeleteProjects')}
                  canCopy={hasPermission('canCreateProjects')}
                  canView={hasPermission('canViewProjects')}
                />
              ))
                          ) : (
                <div className="empty-state">
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📧</div>
                  <h3>No Templates Found</h3>
                  <p>Create your first email template to get started!</p>
                  {hasPermission('canCreateProjects') && (
                    <button 
                      onClick={() => navigate('/email-builder')}
                      style={{
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        color: 'white',
                        border: 'none',
                        padding: '12px 24px',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        marginTop: '1rem',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <FiPlus /> Create Template
                    </button>
                  )}
                  {!hasPermission('canCreateProjects') && (
                    <p style={{ color: '#ef4444', marginTop: '1rem', fontSize: '0.9rem' }}>
                      You do not have permission to create projects
                    </p>
                  )}
                </div>
              )}
          </div>
        )}
      </div>
      {/* Modal for delete confirmation */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Delete Template</h3>
            <p>Are you sure you want to delete <b>{templateToDelete?.templateName}</b>?</p>
            <input
              type="text"
              placeholder="Enter reason for deletion"
              value={deleteReason}
              onChange={e => setDeleteReason(e.target.value)}
              className="modal-input"
            />
            <div className="modal-actions">
              <button onClick={confirmDelete} disabled={!deleteReason}>Delete</button>
              <button onClick={closeModal} className="modal-cancel">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for copy confirmation */}
      {showCopyModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Copy Template</h3>
            <p>Copy template <b>{templateToCopy?.templateName}</b> with a new name:</p>
            <input
              type="text"
              placeholder="Enter new template name"
              value={newTemplateName}
              onChange={e => setNewTemplateName(e.target.value)}
              className="modal-input"
            />
            <div className="modal-actions">
              <button onClick={confirmCopy} disabled={!newTemplateName.trim() || isCopying}>
                {isCopying ? 'Copying...' : 'Copy'}
              </button>
              <button onClick={closeCopyModal} className="modal-cancel">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for viewing template */}
      {showViewModal && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Template Preview: {templateToView?.templateName}</h3>
              <button onClick={closeViewModal} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <div className="template-preview-container">
                <div className="template-preview-header">
                  <div className="template-info-preview">
                    <p><strong>🎨 Template Name:</strong> <span style={{ 
                      background: 'linear-gradient(135deg, #ff6b6b, #ee5a24)', 
                      color: 'white', 
                      padding: '6px 12px', 
                      borderRadius: '12px', 
                      fontSize: '0.9rem',
                      fontWeight: '700',
                      textShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}>{templateToView?.templateName}</span></p>
                    <p><strong>📅 Created:</strong> <span style={{ 
                      background: 'linear-gradient(135deg, #4ecdc4, #44a08d)', 
                      color: 'white', 
                      padding: '6px 12px', 
                      borderRadius: '12px', 
                      fontSize: '0.9rem',
                      fontWeight: '600'
                    }}>{templateToView?.createdAt ? new Date(templateToView.createdAt).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : 'Unknown'}</span></p>
                    <p><strong>🔄 Last Updated:</strong> <span style={{ 
                      background: 'linear-gradient(135deg, #a8e6cf, #88d8c0)', 
                      color: '#2d3436', 
                      padding: '6px 12px', 
                      borderRadius: '12px', 
                      fontSize: '0.9rem',
                      fontWeight: '600'
                    }}>{templateToView?.updatedAt ? new Date(templateToView.updatedAt).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : 'Unknown'}</span></p>
                    <p><strong>🆔 Template ID:</strong> <code style={{ 
                      background: 'linear-gradient(135deg, #feca57, #ff9ff3)', 
                      color: '#2d3436', 
                      padding: '8px 12px', 
                      borderRadius: '12px', 
                      fontSize: '0.85rem',
                      fontFamily: 'monospace',
                      fontWeight: '700',
                      border: '2px solid rgba(255,255,255,0.3)'
                    }}>{templateToView?.TemplateId || templateToView?._id}</code></p>
                    <p><strong>📊 Content Length:</strong> <span style={{ 
                      background: 'linear-gradient(135deg, #45b7d1, #96ceb4)', 
                      color: 'white', 
                      padding: '6px 12px', 
                      borderRadius: '12px', 
                      fontSize: '0.9rem',
                      fontWeight: '600'
                    }}>{templateToView?.html ? `${templateToView.html.length.toLocaleString()} characters` : '0 characters'}</span></p>
                    <p><strong>🎨 Template Type:</strong> <span style={{ 
                      background: 'linear-gradient(135deg, #ff6b6b, #4ecdc4, #45b7d1, #96ceb4, #feca57, #ff9ff3)', 
                      color: 'white', 
                      padding: '8px 16px', 
                      borderRadius: '20px', 
                      fontSize: '0.9rem',
                      fontWeight: '800',
                      textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                      border: '2px solid rgba(255,255,255,0.3)'
                    }}>✨ Premium Email Template ✨</span></p>
                  </div>
                </div>
                <div className="template-html-preview">
                  <h4>Email Preview:</h4>
                  <div 
                    className="email-preview-frame"
                    dangerouslySetInnerHTML={{ __html: templateToView?.html || '<div style="text-align: center; padding: 40px; color: #6b7280; font-style: italic;">No HTML content available</div>' }}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              {hasPermission('canEditProjects') && (
                <button onClick={() => handleEdit(templateToView)} className="edit-btn">
                  <FiEdit2 /> Edit Template
                </button>
              )}
              {hasPermission('canCreateProjects') && (
                <button onClick={() => handleCopy(templateToView)} className="copy-btn">
                  <FiCopy /> Copy Template
                </button>
              )}
              <button onClick={closeViewModal} className="close-btn">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Template Selection Modal */}
      {showTemplateModal && (
        <div className="modal-overlay" onClick={() => setShowTemplateModal(false)}>
          <div className="modal-content template-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Select Email Template</h3>
              <button onClick={() => setShowTemplateModal(false)}>×</button>
            </div>
            
            <div className="modal-body">
              {templatesLoading ? (
                <div className="loading-templates">
                  <p>Loading templates...</p>
                </div>
              ) : templates.length === 0 ? (
                <div className="no-templates">
                  <p>No templates found.</p>
                  <p>Create templates in the email builder first, or create a sample template to get started.</p>
                  <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
                    Debug: Templates array length: {templates.length}
                  </p>
                  <button 
                    onClick={createSampleTemplates}
                    className="create-sample-template-btn"
                    disabled={templatesLoading}
                  >
                    <FiPlus /> Create Sample Templates (3)
                  </button>
                </div>
              ) : (
                <div className="templates-grid">
                  <p style={{ fontSize: '12px', color: '#666', marginBottom: '10px' }}>
                    Debug: Found {templates.length} templates
                  </p>
                  {templates.map(template => (
                    <div 
                      key={template.TemplateId || template._id} 
                      className="template-item"
                    >
                      <div className="template-preview">
                        <div 
                          className="template-html-preview"
                          dangerouslySetInnerHTML={{ __html: template.html }}
                        />
                      </div>
                      <div className="template-info">
                        <h4>{template.templateName}</h4>
                        <p>Created: {new Date(template.createdAt || template.updatedAt).toLocaleDateString()}</p>
                        <p style={{ fontSize: '10px', color: '#999' }}>
                          ID: {template.TemplateId || template._id}
                        </p>
                      </div>
                      <div className="template-actions">
                        {hasPermission('canEditProjects') && (
                          <button 
                            className="template-action-btn edit"
                            onClick={(e) => {
                              e.stopPropagation();
                              selectTemplate(template);
                            }}
                          >
                            <FiEdit2 /> Edit
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button onClick={() => setShowTemplateModal(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default Projects; 